import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useFavorites } from "@/lib/favorites";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "专题", to: "/special" },
  { label: "资讯", to: "/news" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites } = useFavorites();
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b bg-background-50/95 backdrop-blur transition-shadow ${
        scrolled ? "border-background-200 shadow-sm" : "border-background-100"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-page items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 text-background-50">
            <i className="ri-film-line text-xl"></i>
          </span>
          <span className="font-heading text-xl font-bold tracking-tight text-primary-700">
            555<span className="text-accent-500">电影网</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-50 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-secondary-600"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className="hidden items-center rounded-full border border-background-200 bg-background-100 px-3 py-1.5 focus-within:border-secondary-400 focus-within:bg-background-50 lg:flex"
          >
            <div className="flex h-6 w-6 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </div>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索电影 / 电视剧 / 资讯"
              className="w-52 bg-transparent px-1 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-secondary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-secondary-600"
            >
              搜索
            </button>
          </form>

          <Link
            to="/search?q="
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-100 hover:text-secondary-600 lg:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </Link>

          <div className="hidden items-center gap-1 rounded-full bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-700 sm:flex">
            <i className="ri-heart-3-fill"></i>
            <span>收藏 {favorites.length}</span>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
            aria-label="菜单"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          ref={menuRef}
          className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden"
        >
          <form onSubmit={handleSearch} className="mb-3 flex items-center gap-2">
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索电影 / 电视剧 / 资讯"
              className="h-10 flex-1 rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 outline-none focus:border-secondary-400"
            />
            <button
              type="submit"
              className="h-10 whitespace-nowrap rounded-md bg-secondary-500 px-4 text-sm font-medium text-background-50"
            >
              搜索
            </button>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive(item.to)
                    ? "bg-primary-50 text-primary-700"
                    : "bg-background-100 text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}